import Lottie from 'lottie-react'
import { DARK_TREATMENTS, type DarkTreatment } from './darkTreatment'

/**
 * One implementation for every Lottie icon added under DMX-045.
 *
 * WHY THIS EXISTS. The 23 icons this replaced were 23 separate files, each importing one
 * JSON and rendering the same nine lines. 690 lines encoding 23 asset paths. They were
 * written that way to match the 19 older components in this directory, which are the same
 * shape, but "it matches convention" is a weak defence when the convention is unexamined
 * copy-paste, and 23 new files is the point where the cost becomes obvious.
 *
 * The argument FOR separate files is that they let the bundler split per icon. That is not
 * true here: registry.ts imports every icon eagerly, so all of them land in one chunk
 * whether they live in 23 files or one. The splitting argument actually favours this shape,
 * because there is now a single place to add a dynamic import if the chunk ever needs
 * breaking up.
 *
 * WHY THE 19 OLDER COMPONENTS WERE LEFT ALONE. They have 28 direct consumers across Home,
 * Services, About and TechShowcase, all already reviewed and signed off. Collapsing them
 * would mean re-touching approved work to tidy code that was not part of this change. That
 * is its own ticket, not this one.
 */

export interface LottieIconProps {
  className?: string
  size?: number
  /** How the icon handles a dark surface. See darkTreatment.ts for what each costs. */
  darkTreatment?: DarkTreatment
}

/**
 * Builds an icon component from one animation.
 *
 * `displayName` is set so React DevTools and test output name the icon rather than showing
 * a wall of anonymous components, which is the one thing the per-file version gave for free.
 */
export const createLottieIcon = (animationData: unknown, displayName: string) => {
  const LottieIconComponent = ({
    className = 'w-12 h-12',
    size,
    darkTreatment = 'silhouette',
  }: LottieIconProps) => (
    <div className={className} style={{ aspectRatio: '1 / 1', contain: 'layout' }}>
      {/*
        Joined and filtered rather than interpolated: DARK_TREATMENTS.none is an empty
        string, so a template literal emitted `class=" w-full h-full"` with a leading
        space on every opted-out icon.
      */}
      <div className={[DARK_TREATMENTS[darkTreatment], 'w-full h-full'].filter(Boolean).join(' ')}>
        <Lottie
          animationData={animationData}
          loop={true}
          autoplay={true}
          style={{ width: size || '100%', height: size || '100%' }}
          rendererSettings={{
            preserveAspectRatio: 'xMidYMid meet',
          }}
        />
      </div>
    </div>
  )
  LottieIconComponent.displayName = displayName
  return LottieIconComponent
}
